import React, { useCallback, useMemo, useState } from 'react'
import { Logo, Logo2 } from '../../assests/ExportImageIcon'
import useSabraahVoice from './useSabraahVoice'
import './SabraahAiFloater.css'

const ALWAYS_KEY = 'sabraah_always_listen'
const ASKED_KEY = 'sabraah_always_listen_asked'

function readAlwaysListen() {
  try {
    return window.localStorage.getItem(ALWAYS_KEY) === 'on'
  } catch {
    return false
  }
}

function readAsked() {
  try {
    return window.localStorage.getItem(ASKED_KEY) === '1'
  } catch {
    return false
  }
}

function persistAlways(on) {
  try {
    window.localStorage.setItem(ALWAYS_KEY, on ? 'on' : 'off')
    window.localStorage.setItem(ASKED_KEY, '1')
  } catch {
    /* ignore */
  }
}

function toAppUrl(path) {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path
  const prefix = path.startsWith('/') ? path : `/${path}`
  return `${window.location.origin}${prefix}`
}

function offeringRows(offerings) {
  if (!offerings || typeof offerings !== 'object') return []
  const groups = [
    ['flights', 'Flights'],
    ['fares', 'Fare types'],
    ['meals', 'Meals'],
    ['baggage', 'Extra baggage'],
    ['addons', 'Add-ons'],
    ['trains', 'Trains'],
    ['hotels', 'Hotels'],
    ['venues', 'Venues'],
    ['events', 'Events'],
    ['goals', 'Actions'],
  ]
  const rows = []
  groups.forEach(([key, title]) => {
    const items = offerings[key]
    if (!Array.isArray(items)) return
    items.forEach((item, index) => {
      const cabin = String(item.cabin || item.travel_class || '').replace(/_/g, ' ')
      const cabinBit = cabin ? `${cabin} class` : ''
      const when = item.departure_time && item.arrival_time
        ? `${item.departure_time} → ${item.arrival_time}`
        : ''
      const number = item.train_number || (key === 'trains' ? item.id : '')
      const name = item.name || item.airline || item.id || `${title} ${index + 1}`
      rows.push({
        id: `${key}-${index}`,
        title: number && String(number) !== String(name) ? `${number} ${name}` : name,
        meta: [cabinBit, when, item.price_label || '', item.hint || item.availability_status || '']
          .filter(Boolean)
          .join(' · '),
        choose: item.name && (key === 'fares' || key === 'meals' || key === 'baggage' || key === 'addons')
          ? item.name
          : `Option ${index + 1}`,
        bookingUrl: item.booking_url || '',
      })
    })
  })
  return rows
}

function renderMessageText(text) {
  const chunks = String(text || '').split(/(https?:\/\/[^\s]+|\/event-[^\s]+|\/venue[^\s]*|\/hotel[^\s]*)/g)
  return chunks.map((chunk, index) => {
    const isLink = /^https?:\/\//i.test(chunk) || chunk.startsWith('/event-') || chunk.startsWith('/venue') || chunk.startsWith('/hotel')
    if (!isLink) return chunk
    const href = toAppUrl(chunk)
    const label = chunk.startsWith('/event-') || /event-booking-detail/.test(chunk) || /venue/.test(chunk) || /hotel-detail/.test(chunk)
      ? 'Open booking'
      : chunk
    return (
      <a key={`${href}-${index}`} href={href} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    )
  })
}

// TODO: remove once the past-searches API is wired up
const MIN = 60 * 1000
const DUMMY_HISTORY = [
  { id: 'dummy-1', text: 'Flights from Delhi to Dubai next Friday', timestamp: Date.now() - 5 * MIN },
  { id: 'dummy-2', text: 'Book a 5-star hotel in Goa for 3 nights', timestamp: Date.now() - 42 * MIN },
  { id: 'dummy-3', text: 'Cheapest flight Mumbai to Bangkok in December', timestamp: Date.now() - 3 * 60 * MIN },
  { id: 'dummy-4', text: 'Honeymoon package for Maldives under 1.5 lakh', timestamp: Date.now() - 20 * 60 * MIN },
  { id: 'dummy-5', text: 'Events happening in Bangalore this weekend', timestamp: Date.now() - 2 * 24 * 60 * MIN },
  { id: 'dummy-6', text: 'Round trip Bangalore to Singapore for 2 adults', timestamp: Date.now() - 5 * 24 * 60 * MIN },
]

function formatHistoryTime(timestamp) {
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ''
  const diffMs = Date.now() - date.getTime()
  const diffMin = Math.round(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
}

const SabraahAiFloater = () => {
  const [open, setOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [alwaysListen, setAlwaysListen] = useState(readAlwaysListen)
  const [showAsk, setShowAsk] = useState(() => !readAsked())

  const onWakeOpen = useCallback(() => setOpen(true), [])
  const denyAlways = useCallback(() => {
    persistAlways(false)
    setAlwaysListen(false)
    setShowAsk(false)
  }, [])
  const voice = useSabraahVoice({
    alwaysListen,
    onWakeOpen,
    onAlwaysListenDenied: denyAlways,
  })
  const cards = useMemo(() => offeringRows(voice.offerings), [voice.offerings])

  const openAndListen = () => {
    setOpen(true)
    if (alwaysListen) {
      voice.primeMicrophone()
      return
    }
    if (!voice.listening && !voice.thinking && !voice.speaking) {
      voice.startListening()
    }
  }

  const allowAlways = () => {
    persistAlways(true)
    setAlwaysListen(true)
    setShowAsk(false)
    setOpen(true)
    voice.primeMicrophone()
  }

  const toggleAlways = (checked) => {
    persistAlways(checked)
    setAlwaysListen(checked)
    setShowAsk(false)
    if (checked) voice.primeMicrophone()
  }

  const handleFabClick = () => {
    if (!open) {
      if (alwaysListen) voice.primeMicrophone()
      openAndListen()
      return
    }
    if (voice.listening) voice.stopListening()
    else openAndListen()
  }

  return (
    <div className="sabraahFloater">
      {showAsk && !open && (
        <div className="sabraahAsk" role="dialog" aria-label="Always listen for Sabraah">
          <p>
            Allow Sabraah AI to always listen? Say “Sabraah” anytime and the assistant will open and start talking.
          </p>
          <div className="sabraahAsk__actions">
            <button type="button" className="sabraahAsk__allow" onClick={allowAlways}>
              Yes, always listen
            </button>
            <button type="button" className="sabraahAsk__deny" onClick={denyAlways}>
              Not now
            </button>
          </div>
        </div>
      )}

      {open && (
        <section className="sabraahPanel" aria-label="Sabraah AI">
          <div className="sabraahPanel__head">
            <div className="sabraahPanel__brand">
              <strong>Sabraah</strong>
              <span>Let’s make it unforgettable</span>
            </div>
            <div className="sabraahPanel__headActions">
              <button
                type="button"
                className="sabraahPanel__historyBtn"
                onClick={() => setHistoryOpen((prev) => !prev)}
                aria-label="Past searches"
                aria-expanded={historyOpen}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 7v5l3.5 2M21 12a9 9 0 1 1-3.5-7.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M21 3v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                type="button"
                className="sabraahPanel__close"
                onClick={() => { setOpen(false); setHistoryOpen(false) }}
                aria-label="Close"
              >
                ×
              </button>

              {historyOpen && (
                <div className="sabraahHistoryPanel" role="dialog" aria-label="Past searches">
                  <div className="sabraahHistoryPanel__head">
                    <span>Past Searches</span>
                    {voice.history.length > 0 && (
                      <button type="button" onClick={voice.clearHistory}>Clear</button>
                    )}
                  </div>
                  <div className="sabraahHistoryPanel__list">
                    {(voice.history.length ? voice.history : DUMMY_HISTORY).map((item) => (
                      <button
                        type="button"
                        key={item.id}
                        className="sabraahHistoryPanel__item"
                        onClick={() => {
                          setHistoryOpen(false)
                          voice.sendText(item.text)
                        }}
                      >
                        <span className="sabraahHistoryPanel__itemText">{item.text}</span>
                        <span className="sabraahHistoryPanel__itemTime">{formatHistoryTime(item.timestamp)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="sabraahPanel__status">
            <span className={`sabraahPanel__dot${voice.connected === false ? ' error' : voice.connected ? '' : ' warn'}`} />
            {voice.status}
          </div>

          <div className={`sabraahOrb ${voice.mode}`} aria-hidden="true">
            <span className="sabraahOrb__ring" />
            <span className="sabraahOrb__core" />
          </div>

          <label className="sabraahPanel__wake">
            <input
              type="checkbox"
              checked={alwaysListen}
              disabled={!voice.supportsWake}
              onChange={(event) => toggleAlways(event.target.checked)}
            />
            Always listen for “Sabraah”
          </label>

          <div className="sabraahPanel__log" aria-live="polite">
            {voice.messages.length === 0 && (
              <p className="sabraahPanel__error" style={{ color: '#5c6d68' }}>
                Say “Hey Sabraah”, then tell me what’s on your mind.
              </p>
            )}
            {voice.messages.map((msg) => (
              <div key={msg.id} className={`sabraahBubble ${msg.role}`}>
                <span>{msg.role === 'user' ? 'You' : 'Sabraah'}</span>
                {renderMessageText(msg.text)}
              </div>
            ))}
          </div>

          {voice.bookingUrl ? (
            <a
              className="sabraahOfferings__cta"
              href={toAppUrl(voice.bookingUrl)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open booking
            </a>
          ) : null}

          {cards.length > 0 && (
            <div className="sabraahOfferings">
              {cards.map((card) => (
                <div key={card.id} className="sabraahOfferings__row">
                  <button type="button" onClick={() => voice.sendText(card.choose)}>
                    <strong>{card.title}</strong>
                    {card.meta ? <em>{card.meta}</em> : null}
                  </button>
                  {card.bookingUrl ? (
                    <a
                      className="sabraahOfferings__book"
                      href={toAppUrl(card.bookingUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Book
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          {voice.error ? <p className="sabraahPanel__error">{voice.error}</p> : null}

          <form
            className="sabraahPanel__compose"
            onSubmit={(event) => {
              event.preventDefault()
              const message = draft.trim()
              if (!message) return
              setDraft('')
              voice.sendText(message)
            }}
          >
            <input
              type="text"
              value={draft}
              placeholder="Type: book an event"
              onChange={(event) => setDraft(event.target.value)}
              disabled={voice.thinking}
            />
            <button type="submit" disabled={voice.thinking || !draft.trim()}>
              Send
            </button>
          </form>

          <div className="sabraahPanel__controls">
            {voice.listening ? (
              <button type="button" className="sabraahPanel__btn sabraahPanel__btn--ghost" onClick={() => voice.stopListening()}>
                Stop
              </button>
            ) : (
              <button type="button" className="sabraahPanel__btn sabraahPanel__btn--primary" onClick={() => voice.startListening()}>
                Talk
              </button>
            )}
            <button type="button" className="sabraahPanel__btn sabraahPanel__btn--ghost" onClick={() => voice.setMuted((prev) => !prev)}>
              {voice.muted ? 'Unmute' : 'Mute'}
            </button>
            <button type="button" className="sabraahPanel__btn sabraahPanel__btn--ghost" onClick={voice.resetConversation}>
              Reset
            </button>
          </div>
        </section>
      )}

      {alwaysListen && !open ? (
        <div className="sabraahWakeHint">Listening for “Hey Sabraah”</div>
      ) : null}

      <button
        type="button"
        className={`sabraahFab ${voice.mode}`}
        onClick={handleFabClick}
        aria-label={open ? 'Talk to Sabraah' : 'Open Sabraah AI'}
      >
        {(voice.listening || alwaysListen) && !open ? <span className="sabraahFab__pulse" /> : null}
        <img src={Logo2} alt="" />
      </button>
    </div>
  )
}

export default SabraahAiFloater
